import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../utils/axiosInstance";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "0.00";

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const COVERAGE_CLS = {
  "Fully covered":     "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered":       "text-red-500 font-medium",
};

const HEADERS = [
  "Code", "Linked invoices", "Coverage by invoices",
  "Vendor", "Amount", "Linked amount", "Unlinked amount",
  "Currency", "Payment method", "Paid at",
];

const AdminVendorPaymentsPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState(
    sessionStorage.getItem("paymentSuccessMsg") || ""
  );

  useEffect(() => {
    sessionStorage.removeItem("paymentSuccessMsg");
    api.get("/project-finances/vendor-payments", { withCredentials: true })
      .then((res) => setPayments(res.data.data || []))
      .catch(() => setError("Failed to fetch vendor payments"))
      .finally(() => setLoading(false));
  }, []);

  const getVendorName = (vendor) => {
    if (!vendor) return "—";
    if (vendor.company_name) return vendor.company_name;
    const u = vendor.primary_users;
    return u ? `${u.first_name} ${u.last_name}`.trim() : "—";
  };

  return (
    <div className="space-y-4">
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex justify-between items-center text-sm text-green-800">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg("")} className="text-green-600 hover:text-green-800 font-bold">✕</button>
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Vendor Payments</h1>
        <div className="flex items-center gap-2 text-sm">
          <button className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded">
            New payment
          </button>
          <button className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded">
            Import from Excel
          </button>
          <button className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded">
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
        </div>
      )}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{payments.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold text-center whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                        No vendor payments found.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p, i) => {
                      const coverageCls = COVERAGE_CLS[p.coverage_by_invoices] ?? "text-gray-600";
                      const pmType = p.paymentMethod?.payment_method?.replace(/_/g, " ") || "—";
                      const vendorName = getVendorName(p.vendor);
                      const rowBg = i % 2 === 0 ? "bg-gray-50" : "bg-white";

                      return (
                        <tr
                          key={p.id}
                          className={`${rowBg} hover:bg-blue-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendor/payment/${p.id}`}
                              className="text-blue-600 hover:text-blue-800 font-medium underline"
                            >
                              {p.payment_code}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {p.linkedInvoice ? (
                              <Link to={`/vendor/invoice/${p.linkedInvoice.id}`} className="text-blue-600 hover:underline font-medium">
                                {p.linkedInvoice.invoice_code}
                              </Link>
                            ) : "—"}
                          </td>
                          <td className={`px-3 py-2 text-xs text-center ${coverageCls}`}>
                            {p.coverage_by_invoices || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {p.vendor_id ? (
                              <Link to={`/vendors/${p.vendor_id}`} className="text-blue-600 hover:underline font-medium">
                                {vendorName}
                              </Link>
                            ) : vendorName}
                          </td>
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">{fmtNum(p.amount)}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">{fmtNum(p.linked_amount)}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">{fmtNum(p.unlinked_amount)}</td>
                          <td className="px-3 py-2 text-xs text-center">{p.currency || "—"}</td>
                          <td className="px-3 py-2 text-xs capitalize text-center">{pmType}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">{fmtDate(p.paid_at)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminVendorPaymentsPage;
